import { useState, type ReactNode } from 'react';

import type { FeedHealth, SubscriptionSummary } from '../api/types.js';

/**
 * What a reader is told about a feed that has stopped working (M2.4.3).
 *
 * Shown above the timeline when a single feed is selected and that feed is not
 * healthy. Above the timeline rather than in a settings screen, because the
 * question it answers — "why is there nothing here?" — is asked while looking
 * at the nothing.
 *
 * ## It never appears for a quiet feed
 *
 * A feed with no recent items and a working fetch is `ok`, so this renders
 * nothing. That is the point of the whole feature: silence from a publisher
 * is not a fault, and a banner explaining it would turn somebody's favourite
 * infrequent writer into a warning.
 *
 * ## A dead feed is offered both ways out
 *
 * M2.4.3's third criterion: unsubscribe, **or** a URL correction. The
 * correction is the interesting one, because feeds are shared (D3) — the feed
 * record belongs to everyone subscribed to it, so its address cannot be
 * edited by one subscriber. What "correct the URL" therefore means is:
 * subscribe to the new address and unsubscribe from the dead one, keeping the
 * folder. The read state does not survive, because it belongs to the old
 * feed's sequence numbers and there is no honest way to map them onto a feed
 * that may be an entirely different document. The dialog says so rather than
 * letting somebody discover it.
 */

export interface FeedHealthBannerProps {
  readonly subscription: SubscriptionSummary;
  readonly onUnsubscribe: (feedId: string, title: string) => void;
  /** Subscribes to `url` in the dead feed's folder, then removes the dead one. */
  readonly onCorrectUrl: (feedId: string, url: string) => Promise<void>;
}

export function FeedHealthBanner(props: FeedHealthBannerProps): ReactNode {
  const [correcting, setCorrecting] = useState(false);
  const health = props.subscription.health;

  if (health === undefined || health.status === 'ok') {
    return null;
  }

  return (
    <aside className={`health health--${health.status}`} role="status">
      <p className="health__headline">
        <span aria-hidden="true" className="health__mark">
          {health.status === 'dead' ? '⊘' : '!'}
        </span>
        {headline(health, props.subscription.title)}
      </p>

      {health.detail !== null && <p className="health__detail">{health.detail}</p>}

      <p className="health__facts">{facts(health)}</p>

      {/*
       * Only a dead feed is offered a way out. A feed that is merely failing
       * is one the publisher will probably fix, and inviting somebody to
       * remove it would be advice rather than information.
       */}
      {health.status === 'dead' &&
        (correcting ? (
          <CorrectUrl
            subscription={props.subscription}
            onSubmit={async (url) => {
              await props.onCorrectUrl(props.subscription.feedId, url);
            }}
            onCancel={() => {
              setCorrecting(false);
            }}
          />
        ) : (
          <div className="health__actions">
            <button
              className="button button--small"
              onClick={() => {
                setCorrecting(true);
              }}
            >
              It has moved — fix the address
            </button>
            <button
              className="button button--small"
              onClick={() => {
                props.onUnsubscribe(props.subscription.feedId, props.subscription.title);
              }}
            >
              Unsubscribe
            </button>
          </div>
        ))}
    </aside>
  );
}

/* --------------------------------------------------------------- wording --- */

function headline(health: FeedHealth, title: string): string {
  switch (health.status) {
    case 'dead':
      return `${title} is no longer publishing at this address.`;
    case 'erroring':
      return `We have not been able to fetch ${title}.`;
    default:
      // `slow`: deliberately the mildest sentence available, because the usual
      // cause is a publisher who restarted a server.
      return `${title} is not answering reliably.`;
  }
}

/**
 * The two dates, in a fixed order.
 *
 * "When it last worked" is the fact that separates a feed that broke this
 * morning from one that has never worked at all, and it is the first thing
 * somebody looking at a broken feed wants. "When it last published" is there
 * so a reader can tell whether they are missing anything: a feed that broke
 * three days after its final post has probably simply ended.
 */
function facts(health: FeedHealth): string {
  const parts: string[] = [];

  parts.push(
    health.lastSuccessfulFetchAt === null
      ? 'It has never been fetched successfully.'
      : `Last fetched successfully on ${formatDate(health.lastSuccessfulFetchAt)}.`,
  );

  if (health.lastItemAt !== null) {
    parts.push(`Its most recent item is from ${formatDate(health.lastItemAt)}.`);
  }

  if (health.consecutiveErrors > 0) {
    parts.push(
      health.consecutiveErrors === 1
        ? 'One failed attempt so far.'
        : `${String(health.consecutiveErrors)} failed attempts so far.`,
    );
  }

  return parts.join(' ');
}

/** A date somebody can check against their own memory, not a relative age. */
function formatDate(timestamp: string): string {
  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return 'an unknown date';
  }

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/* ------------------------------------------------------------ correction --- */

function CorrectUrl(props: {
  readonly subscription: SubscriptionSummary;
  readonly onSubmit: (url: string) => Promise<void>;
  readonly onCancel: () => void;
}): ReactNode {
  const [url, setUrl] = useState(props.subscription.siteUrl ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  return (
    <form
      className="health__correction"
      onSubmit={(event) => {
        event.preventDefault();
        const trimmed = url.trim();
        if (trimmed === '') {
          return;
        }
        setBusy(true);
        setError(undefined);
        props.onSubmit(trimmed).catch((caught: unknown) => {
          setError(
            caught instanceof Error && caught.message !== ''
              ? caught.message
              : 'That address could not be added.',
          );
          setBusy(false);
        });
      }}
    >
      <label className="field">
        <span className="field__label">The new address</span>
        <input
          className="field__input"
          type="text"
          value={url}
          autoFocus
          disabled={busy}
          placeholder="example.com — or the feed URL, if you have it"
          onChange={(event) => {
            setUrl(event.target.value);
          }}
        />
      </label>

      {/*
       * Said up front, not discovered afterwards. Feeds are shared (D3), so
       * this subscribes to the new address and removes the old subscription —
       * which means read state cannot come with it, because it is expressed
       * in the old feed's sequence numbers.
       */}
      <p className="health__note">
        This adds the new address{' '}
        {props.subscription.folderId === undefined ? '' : 'in the same folder '}
        and removes the old one. What you have already read here will not carry across;
        anything you have archived is kept.
      </p>

      {error !== undefined && (
        <p className="auth__error" role="alert">
          {error}
        </p>
      )}

      <div className="health__actions">
        <button className="button button--small" type="submit" disabled={busy}>
          {busy ? 'Moving…' : 'Use this address'}
        </button>
        <button
          className="button button--small"
          type="button"
          disabled={busy}
          onClick={props.onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
